"use client";

import { useRef } from "react";

import Link from "next/link";

import { ArrowUpRight, CalendarBlank, ChartBar, Users } from "@phosphor-icons/react";
import { motion, useScroll, useTransform, type Variants } from "motion/react";

import { FeaturesSection } from "./features-section";
import { Footer } from "./footer";

const easeCubic = [0.32, 0.72, 0, 1] as const;

const staggerContainer: Variants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
    },
  },
};

const fadeUpVariant: Variants = {
  hidden: { opacity: 0, y: 80, filter: "blur(16px)" },
  show: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: {
      duration: 1.4,
      ease: easeCubic,
    },
  },
};

export function LandingPage() {
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"],
  });

  const yBackground = useTransform(scrollYProgress, [0, 1], ["0%", "50%"]);

  return (
    <div
      ref={containerRef}
      className="bg-[#F9F9FB] text-[#0A0A0A] font-sans selection:bg-[#0055FF] selection:text-white"
      style={{ fontFamily: "var(--font-geist-sans), sans-serif" }}
      dir="ltr"
      lang="en"
    >
      {/* 
        THE HERO (Cinematic Center)
      */}
      <section className="relative min-h-[100dvh] w-full flex flex-col items-center justify-center bg-[#F9F9FB] pt-32 pb-20 overflow-hidden">
        {/* Background Mesh */}
        <motion.div className="absolute inset-0 z-0 pointer-events-none" style={{ y: yBackground }}>
          <div className="absolute top-[-10%] right-[-5%] w-[60vw] h-[80vh] bg-gradient-to-bl from-[#EAEAEF] to-transparent rounded-full blur-[120px] opacity-80" />
          <div className="absolute bottom-[-20%] left-[-10%] w-[50vw] h-[60vh] bg-gradient-to-tr from-[#F4F4F6] to-transparent rounded-full blur-[100px] opacity-60" />
        </motion.div>

        <div className="relative z-10 w-full max-w-6xl mx-auto px-6 flex flex-col items-center text-center">
          <motion.div
            variants={fadeUpVariant}
            initial="hidden"
            animate="show"
            className="mb-8 rounded-full bg-black/5 border border-black/5 px-4 py-1.5 text-[10px] uppercase tracking-[0.2em] font-semibold text-black/60"
          >
            The Vanguard of Educational Management
          </motion.div>

          <motion.h1
            variants={fadeUpVariant}
            initial="hidden"
            animate="show"
            className="text-6xl md:text-[clamp(4rem,7vw,7rem)] leading-[0.9] font-bold tracking-tighter mb-8 text-[#0A0A0A] max-w-5xl"
          >
            Command Your <span className="text-black/30">Institution.</span>
          </motion.h1>

          <motion.p
            variants={fadeUpVariant}
            initial="hidden"
            animate="show"
            transition={{ delay: 0.1 }}
            className="text-base sm:text-lg md:text-xl lg:text-2xl text-black/50 max-w-[90vw] md:max-w-4xl lg:max-w-5xl mb-10 md:mb-12 font-medium leading-relaxed text-balance mx-auto"
            style={{ fontFamily: "var(--font-geist-mono), monospace" }}
          >
            The elite educational management infrastructure built for the modern Algerian academy. Streamline
            administration, master your finances, and elevate your prestige.
          </motion.p>

          <motion.div
            variants={fadeUpVariant}
            initial="hidden"
            animate="show"
            transition={{ delay: 0.2 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-6 w-full sm:w-auto mb-20"
          >
            <Link
              href="/request-demo"
              className="group relative inline-flex items-center justify-center rounded-full bg-[#0A0A0A] text-white pl-8 pr-2 py-2 font-semibold text-sm transition-all duration-700 ease-[cubic-bezier(0.32,0.72,0,1)] hover:bg-[#0055FF] active:scale-[0.98] w-full sm:w-auto"
            >
              <span className="mr-4">Book a Demo</span>
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center transition-transform duration-700 ease-[cubic-bezier(0.32,0.72,0,1)] group-hover:translate-x-1 group-hover:-translate-y-[1px] group-hover:scale-105">
                <ArrowUpRight weight="light" className="w-4 h-4" />
              </div>
            </Link>
          </motion.div>

          <motion.div
            variants={fadeUpVariant}
            initial="hidden"
            animate="show"
            transition={{ delay: 0.3 }}
            className="relative w-full max-w-6xl mx-auto rounded-[2rem] p-2 bg-black/5 ring-1 ring-black/5"
          >
            <div className="relative rounded-[calc(2rem-0.5rem)] overflow-hidden shadow-2xl bg-white border border-black/10 group">
              <img
                src="/features/overview.webp"
                alt="Dashboard Overview"
                className="w-full h-auto object-cover transform scale-100 transition-transform duration-1000 group-hover:scale-105"
              />
            </div>
          </motion.div>
        </div>
      </section>

      <FeaturesSection />

      {/* 
        SOCIAL PROOF (The Vanguard of Education)
        "The Editorial Split"
      */}
      <section className="px-4 py-32 md:py-48 border-t border-black/5 bg-white">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row w-full gap-16 items-center">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeUpVariant}
            className="w-full md:w-1/2"
          >
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">Trusted by the Nation's Elite.</h2>
            <p className="text-xl text-black/50 font-medium max-w-md">
              The highest-performing academies in Algeria don't run on legacy software. They run on Vokiya.
            </p>
          </motion.div>

          {/* Right side staggered pills */}
          <div className="w-full md:w-1/2 flex flex-col space-y-6">
            {[
              "Lycée d'Excellence Al-Bayan",
              "Institut Supérieur Descartes",
              "Académie des Sciences Oran",
              "El-Othmania Premium Education",
            ].map((name, i) => (
              <motion.div
                key={name}
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{
                  duration: 1,
                  ease: easeCubic,
                  delay: i * 0.1,
                }}
                className={`flex items-center justify-center p-6 rounded-full bg-black/[0.02] border border-black/5 backdrop-blur-sm ${
                  i % 2 === 1 ? "md:translate-x-12" : ""
                }`}
              >
                <span className="font-semibold text-black/80 tracking-wide text-sm uppercase">{name}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 
        THE "FLUID ISLAND" CALL TO ACTION
      */}
      <section className="px-4 py-32 md:py-48 flex justify-center w-full">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-100px" }}
          variants={fadeUpVariant}
          className="w-full max-w-4xl"
        >
          <div className="rounded-[2.5rem] p-2 bg-black/5 ring-1 ring-black/5">
            <div className="rounded-[calc(2.5rem-0.5rem)] bg-[#0A0A0A] p-12 md:p-24 shadow-[inset_0_1px_1px_rgba(255,255,255,0.15)] flex flex-col items-center text-center">
              <h2 className="text-4xl md:text-6xl font-bold tracking-tight text-white mb-6">
                Ready to modernize
                <br />
                your academy?
              </h2>
              <p className="text-lg md:text-xl text-white/60 font-medium max-w-xl mx-auto mb-12">
                Join the exclusive network of institutions operating at peak efficiency.
              </p>
              <Link
                href="/request-demo"
                className="group relative inline-flex items-center justify-center rounded-full bg-white text-[#0A0A0A] pl-8 pr-2 py-2 font-semibold text-sm transition-all duration-700 ease-[cubic-bezier(0.32,0.72,0,1)] hover:bg-[#F0F0F0] active:scale-[0.98]"
              >
                <span className="mr-4">Schedule Your Private Demonstration</span>
                <div className="w-10 h-10 rounded-full bg-black/5 flex items-center justify-center transition-transform duration-700 ease-[cubic-bezier(0.32,0.72,0,1)] group-hover:translate-x-1 group-hover:-translate-y-[1px] group-hover:scale-105">
                  <ArrowUpRight weight="bold" className="w-4 h-4" />
                </div>
              </Link>
            </div>
          </div>
        </motion.div>
      </section>
      <Footer />
    </div>
  );
}
